import React, { useEffect } from 'react';
import {
  Checkbox,
  Dropdown,
  getTheme,
  IDropdownOption,
  IPalette,
  ISpinButtonStyles,
  IStackItemStyles,
  Position,
  SpinButton,
  Stack,
  Text
} from '@fluentui/react';
import { FlowFormProps } from './DetailsForm';
import { TimeFieldGeneric } from './TimeFieldGeneric';
import { ValidationMessage } from '../admin/rules/ValidationMessage';
import { TooltipText } from '../shared';
import { QueueConfiguration } from '../../services';
import { PercentageFieldGeneric } from './PercentageFieldGeneric';
import { settingsDropdownRoleStyles } from '../admin/SettingsStyles';
import { ContentPreviewsGeneric } from './ContentPreviewsGeneric';
import { optionsCaseComparerAlgorithm } from '../admin/rules/Cases';

export const ModerationForm: React.FC<FlowFormProps> = props => {
  const { fonts, palette } = getTheme();

  const formIsValid = (): boolean => {
    return (
      !!props.currentQueue.caseOpenLifetimeInSeconds &&
      !!props.currentQueue.caseAssignedLifetimeInSeconds &&
      !!props.currentQueue.caseOpenLifetimeWarningFactor &&
      !!props.currentQueue.defaultCaseComparerAlgorithm
    );
  };

  useEffect(() => {
    if (formIsValid()) {
      props.onFormComplete(true);
    } else {
      props.onFormComplete(false);
    }
  }, [props]);

  const settingsSpinButtonStyles = (palette: IPalette, width: string | number): Partial<ISpinButtonStyles> => {
    const settingsFieldStyles: Partial<ISpinButtonStyles> = {
      spinButtonWrapper: {
        backgroundColor: palette.neutralLighter,
        borderRadius: 4,
        height: 40,
        width: width,
        borderWidth: 0,
        marginTop: 3,
        borderColor: palette.white,
        color: palette.blackTranslucent40
      }
    };
    return settingsFieldStyles;
  };

  const lineStyle: Partial<IStackItemStyles> = {
    root: {
      selectors: {
        '::before': {
          backgroundColor: palette.neutralLight,
          bottom: 0,
          left: 0,
          height: 2,
          width: 700,
          marginTop: 24,
          content: `''`
        }
      }
    }
  };

  // {/* --------- CASE URGENCY SECTION --------- */}
  const onCaseFlaggedChange = (timeInSeconds: number) => {
    const updatedQueue = {
      ...props.currentQueue,
      caseOpenLifetimeInSeconds: timeInSeconds
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  const onCaseTimeLimitChange = (timeInSeconds: number) => {
    const updatedQueue = {
      ...props.currentQueue,
      caseAssignedLifetimeInSeconds: timeInSeconds
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  const onCaseWarningChange = (warningFactor: number) => {
    const updatedQueue = {
      ...props.currentQueue,
      caseOpenLifetimeWarningFactor: warningFactor
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  // {/* --------- CASES AND SETS SECTION --------- */}
  const onCasesPerSetChange = (casesPerSet: number) => {
    const updatedQueue = {
      ...props.currentQueue,
      defaultMaxAssignedCaseCount: casesPerSet
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  const onRequireCommentChange = (requireComment?: boolean) => {
    const updatedQueue = {
      ...props.currentQueue,
      moderatorFeedbackCommentOptional: !requireComment
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  const onSortingChange = (selectedSort?: IDropdownOption<any>) => {
    const updatedQueue = {
      ...props.currentQueue,
      defaultCaseComparerAlgorithm: selectedSort?.key
    } as QueueConfiguration;
    props.onFormChange(updatedQueue);
  };

  // {/* --------- CONTENT PREVIEWS SECTION --------- */}
  const onUxPreferenceChange = (preferenceAttr: string, preference: boolean) => {
    const updatedQueue = {
      ...props.currentQueue
    } as QueueConfiguration;
    updatedQueue.uxPreferences.subjectImage[preferenceAttr] = preference;
    props.onFormChange(updatedQueue);
  };

  return (
    <Stack>
      {/* --------- CASE URGENCY SECTION --------- */}
      <Text className="my-2" variant={'xLarge'}>
        {'Case Urgency'}
      </Text>
      <Stack style={{ marginTop: 16 }} />
      <Text className="my-2" variant={'large'}>
        {'Case Flagged'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`How long a case can remain in an open state before it is flagged for being out of SLA. Minimum setting is 1 minute.`}
      </Text>
      <TimeFieldGeneric value={props.currentQueue.caseOpenLifetimeInSeconds} onComponentChange={onCaseFlaggedChange} />
      <Stack.Item className="pl-2">
        {!props.currentQueue.caseOpenLifetimeInSeconds && (
          <ValidationMessage message={TooltipText.validationTimeMessage} fonts={fonts} />
        )}
      </Stack.Item>
      <Stack style={{ marginTop: 16 }} />
      <Text className="my-2" variant={'large'}>
        {'Case Warning'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`Percentage of how close to the Case Open SLA an open case can get before it gets counted as being Near SLA. Minimum setting is 1%.`}
      </Text>
      <PercentageFieldGeneric
        onChange={(newValue): void => onCaseWarningChange(newValue)}
        value={props.currentQueue.caseOpenLifetimeWarningFactor}
      />
      <Stack style={{ marginTop: 16 }} />
      <Text className="my-2" variant={'large'}>
        {'Case Time Limit'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`How long a set of open cases is assigned to a moderator. Once this time expires, the cases return to the queue for moderation. Minimum setting is 1 minute.`}
      </Text>
      <TimeFieldGeneric
        value={props.currentQueue.caseAssignedLifetimeInSeconds}
        onComponentChange={onCaseTimeLimitChange}
      />
      <Stack.Item className="pl-2">
        {!props.currentQueue.caseAssignedLifetimeInSeconds && (
          <ValidationMessage message={TooltipText.validationTimeMessage} fonts={fonts} />
        )}
      </Stack.Item>
      <Stack styles={lineStyle} style={{ width: 80 }}></Stack>

      {/* --------- CASES AND SETS SECTION --------- */}
      <Stack style={{ marginTop: 32 }} />
      <Text className="my-2" variant={'xLarge'}>
        {'Cases and sets'}
      </Text>
      <Stack style={{ marginTop: 16 }} />
      <Text className="my-2" variant={'large'}>
        {'Cases per set'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`The maximum number of cases within a set that a moderator can be assigned at any given time. Maximum setting is 30 cases.`}
      </Text>
      <SpinButton
        label="Cases"
        onChange={(e, newValue): void => onCasesPerSetChange(parseInt(newValue || ''))}
        styles={settingsSpinButtonStyles(palette, 60)}
        min={1}
        max={30}
        step={1}
        value={props.currentQueue.defaultMaxAssignedCaseCount.toString()}
        labelPosition={Position.top}
      />

      <Stack style={{ marginTop: 16 }} />
      <Text className="my-2" variant={'large'}>
        {'Require case comment'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`If checked, moderators are required to enter a comment before submitting feedback.`}
      </Text>
      <Checkbox label="Require comment" onChange={(e, requireComment) => onRequireCommentChange(requireComment)} />
      <Stack style={{ marginTop: 16 }} />
      <Text className="my-2" variant={'large'}>
        {'Sorting'}
      </Text>
      <Text className="my-2" variant={'medium'} style={{ width: 656 }}>
        {`This changes how cases in the moderation queue are prioritized. Highest priority cases are automatically assigned to the next available moderator.`}
      </Text>
      <Dropdown
        required
        defaultSelectedKey={props.currentQueue.defaultCaseComparerAlgorithm}
        options={optionsCaseComparerAlgorithm}
        onChange={(e, newValue): void => onSortingChange(newValue)}
        styles={settingsDropdownRoleStyles(palette, fonts, 170)}
      />
      <Stack.Item className="pl-2">
        {props.currentQueue.caseAssignedLifetimeInSeconds === 0 && (
          <ValidationMessage message={TooltipText.validationTimeMessage} fonts={fonts} />
        )}
      </Stack.Item>
      <Stack styles={lineStyle} style={{ width: 80 }}></Stack>

      {/* --------- CONTENT PREVIEWS SECTION --------- */}
      <ContentPreviewsGeneric
        displayName={props.currentQueue.displayName}
        imageUxPreferences={props.currentQueue.uxPreferences.subjectImage}
        onUxPreferenceChange={onUxPreferenceChange}
      />
      <Stack styles={lineStyle} style={{ width: 80, marginBottom: 200 }}></Stack>
    </Stack>
  );
};
